// frontend/src/hooks/useAnchorRect.ts
import { useCallback, useLayoutEffect, useState, RefObject } from 'react';

export interface AnchorOffset {
  right: number;
  bottom: number;
}

// Measures an element's distance from the viewport's right/bottom edges.
// Use this instead of guessing a fixed-position offset when the trigger
// element isn't itself position: fixed (e.g. it's absolute within a card),
// so a separately fixed-position element (like a Drawer) can be aligned to
// its actual on-screen position rather than a coordinate system it doesn't
// share.
//
// Recomputes while `active` is true: once on activation (via
// useLayoutEffect, before paint, to avoid a visible jump) and again on
// window resize. Returns null until a measurement exists.
export function useAnchorRect(
  ref: RefObject<HTMLElement | null>,
  active: boolean
): AnchorOffset | null {
  const [offset, setOffset] = useState<AnchorOffset | null>(null);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setOffset({
      right: window.innerWidth - rect.right,
      bottom: window.innerHeight - rect.bottom,
    });
  }, [ref]);

  useLayoutEffect(() => {
    if (!active) return;
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [active, measure]);

  return offset;
}
